import { loadDocumentPages } from './attachments'
import { supabase } from './supabase'
import { buildZip, exportFilename, toCsv, type ZipEntry } from './zip'
import { lastDayOfMonth } from './dates'

/**
 * A month, in one file, for the accountant.
 *
 * spec.md asks for a zip of the originals with structured filenames plus an
 * index. This is the reason ten-year retention is worth anything: the app is
 * where the paperwork lives, so the app has to be able to hand it over
 * without anyone opening a database.
 *
 * The index is the useful half. Belgian accounting software imports a
 * semicolon CSV, and the filename column is what lets a person tie a row back
 * to the scan.
 */

export interface ExportProgress {
  done: number
  total: number
}

export interface ExportResult {
  blob: Blob
  filename: string
  documents: number
  missing: number
}

export async function exportMonth(
  month: string,
  onProgress?: (p: ExportProgress) => void,
): Promise<ExportResult> {
  const from = `${month}-01`
  const to = lastDayOf(month)

  const { data: states, error } = await supabase
    .from('instalment_state')
    .select('instalment_id, obligation_id, amount_cents, paid_cents, due_date, state')
    .gte('due_date', from)
    .lte('due_date', to)
    .order('due_date')
  if (error) throw error

  const obligationIds = [...new Set((states ?? []).map((s) => s.obligation_id as string))]
  if (obligationIds.length === 0) {
    throw new Error('Er staat niets in die maand.')
  }

  const { data: obligations } = await supabase
    .from('obligations')
    .select('id, description, category, iban, payment_reference, source_document_id, party_id, space_id')
    .in('id', obligationIds)
    .is('deleted_at', null)

  const byId = new Map((obligations ?? []).map((o) => [o.id as string, o]))

  const partyIds = [...new Set((obligations ?? []).map((o) => o.party_id).filter(Boolean))] as string[]
  const { data: parties } = partyIds.length
    ? await supabase.from('parties').select('id, name').in('id', partyIds)
    : { data: [] as never[] }
  const partyName = new Map((parties ?? []).map((p) => [p.id as string, p.name as string]))

  const spaceIds = [...new Set((obligations ?? []).map((o) => o.space_id))] as string[]
  const { data: spaces } = spaceIds.length
    ? await supabase.from('spaces').select('id, name').in('id', spaceIds)
    : { data: [] as never[] }
  const spaceName = new Map((spaces ?? []).map((s) => [s.id as string, s.name as string]))

  const entries: ZipEntry[] = []
  const rows: (string | number | null)[][] = [
    ['Vervaldag', 'Leverancier', 'Omschrijving', 'Ruimte', 'Categorie',
     'Bedrag', 'Betaald', 'Status', 'IBAN', 'Mededeling', 'Bestand'],
  ]

  let missing = 0
  const total = (states ?? []).length

  for (const [index, s] of (states ?? []).entries()) {
    const o = byId.get(s.obligation_id as string)
    const supplier = o?.party_id ? (partyName.get(o.party_id as string) ?? '') : ''
    const amountCents = Number(s.amount_cents)

    let filename = ''
    if (o?.source_document_id) {
      try {
        const pages = await loadDocumentPages(o.source_document_id as string)
        for (const [page, doc] of pages.entries()) {
          const bytes = new Uint8Array(await (await fetch(doc.url)).arrayBuffer())
          const name = exportFilename({
            date: s.due_date as string,
            supplier: supplier || String(o.description ?? ''),
            amountCents,
            extension: extensionFor(doc.contentType),
          })
          // Several photographed pages of one invoice keep one base name with
          // a suffix, so they stay next to each other in a folder listing.
          const finalName =
            pages.length > 1 ? name.replace(/(\.\w+)$/, `-p${page + 1}$1`) : name
          entries.push({ name: `${month}/${finalName}`, bytes })
          if (page === 0) filename = finalName
        }
      } catch {
        // A document that will not download must not take the whole month
        // with it. It is counted and reported instead.
        missing++
      }
    } else {
      missing++
    }

    rows.push([
      s.due_date as string,
      supplier,
      String(o?.description ?? ''),
      spaceName.get(o?.space_id as string) ?? '',
      String(o?.category ?? ''),
      euros(amountCents),
      euros(Number(s.paid_cents ?? 0)),
      String(s.state ?? ''),
      String(o?.iban ?? ''),
      String(o?.payment_reference ?? ''),
      filename,
    ])

    onProgress?.({ done: index + 1, total })
  }

  // A BOM, because Excel on Windows reads a plain UTF-8 CSV as Latin-1 and
  // turns every accented supplier name into rubbish.
  const csv = '﻿' + toCsv(rows)
  entries.push({ name: `${month}/index.csv`, bytes: new TextEncoder().encode(csv) })

  return {
    blob: buildZip(entries),
    filename: `familie-${month}.zip`,
    documents: entries.length - 1,
    missing,
  }
}

function euros(cents: number): string {
  return (cents / 100).toFixed(2).replace('.', ',')
}

function extensionFor(contentType: string): string {
  if (contentType === 'application/pdf') return 'pdf'
  if (contentType === 'image/png') return 'png'
  return 'jpg'
}

/** Kept as a name the rest of this file already uses. See core/dates.ts. */
function lastDayOf(month: string): string {
  return lastDayOfMonth(month)
}

export function monthOptions(count = 12, today = new Date()): { value: string; label: string }[] {
  return Array.from({ length: count }, (_, i) => {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    return {
      value,
      label: new Intl.DateTimeFormat('nl-BE', { month: 'long', year: 'numeric' }).format(d),
    }
  })
}
